import React, { useEffect, useRef, useState } from 'react';
import { Bot, Send, Undo2, RotateCcw, Check, Rocket, FileText, MessageSquare, Pencil, Copy, Plus, ArrowRight, Save, Loader2, Circle } from 'lucide-react';
import { api, inputCls, Button, Notice } from './ui';
import { EMPTY, TIMEZONES } from './TeamSetup';
import { EXAMPLES as DEAL_EXAMPLES, EDITS as DEAL_EDITS } from './Deals';
import { outOfScope } from './guard';
import { parseContacts, CONTACT_FIELDS, LogoEditor, logosOf, logosIssue, cleanLogos, MAX_LOGOS } from './signature';

/**
 * Creating a team by answering questions, one at a time, instead of filling
 * in the form. Every question has suggestions to click, and a typed answer
 * works too ("5 to 30 minutes", "9am to 6pm, weekdays", "2, 3, 5").
 *
 * The questions are scripted, not a model: setting up a team costs nothing
 * and says the same thing every time. What it collects is exactly what the
 * Setup form saves, so a team made here is edited there afterwards.
 *
 * Every answer is saved on the server as it is given (/setup-chats), like a
 * chat app's history: a closed tab, a reload or another browser opens the
 * same chat, and the chat that made a team is still there afterwards. The
 * page lists the chats (ChatHistory.jsx) and opens one here.
 *
 * A deal is made right here too: at the deals question the deal desk (the
 * same model and the same deal as on the Deals tab) joins the chat, asks
 * until nothing is missing, and a saved deal is picked for the team.
 */

// Where an earlier version kept the one chat, in this browser only. Read once and moved to the server.
const DRAFT_KEY = 'mars.agentTeam.newTeamChat';

// ── reading typed answers ───────────────────────────────────────────────────

// The whole answer, so a pitch that starts "No KYC, fast payouts" is not a skip.
const SKIP = /^(skip|skip for now|skip it|none|no|nope|nothing|n\/a|-|later|not yet)[.!]?$/i;

const yesNo = (text) => {
  const t = text.trim().toLowerCase();
  if (/^(y|yes|yeah|yep|sure|ok|okay|on|please|of course)\b/.test(t)) return true;
  if (/^(n|no|nope|off|never|don'?t)\b/.test(t)) return false;
  return null;
};

// "500 to 2,000", "1k-3k", "£1.5k" -> [500, 2000], [1000, 3000], [1500]
const amountsIn = (text) => [...String(text).toLowerCase().matchAll(/(\d[\d,]*(?:\.\d+)?)\s*(k|m)?\b/g)]
  .map((m) => Math.round(Number(m[1].replace(/,/g, '')) * (m[2] === 'k' ? 1000 : m[2] === 'm' ? 1000000 : 1)))
  .filter((v) => Number.isFinite(v));

// "15", "5k", "1.5m" -> 15, 5000, 1500000
const firstNumber = (text) => {
  const m = String(text).toLowerCase().replace(/,/g, '').match(/(\d+(?:\.\d+)?)\s*(k|m)?\b/);
  if (!m) return null;
  return Math.round(Number(m[1]) * (m[2] === 'k' ? 1000 : m[2] === 'm' ? 1000000 : 1));
};

// "1-15", "30 min to 2 hours", "1 to 3 hours", "straight away" -> [min, max] in minutes.
// A number without a unit takes the unit of the one after it, so "1 to 3 hours" is hours.
const parseMinutes = (text) => {
  const t = text.toLowerCase();
  if (/straight|immediate|right away|at once|no delay|instant/.test(t)) return [0, 0];
  const parts = [...t.matchAll(/(\d+(?:\.\d+)?)\s*(h(?:ou)?rs?|h\b|m(?:in(?:ute)?s?)?\b)?/g)]
    .map((m) => ({ n: Number(m[1]), unit: m[2] ? (m[2].startsWith('h') ? 60 : 1) : null }));
  if (!parts.length) return null;
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    if (parts[i].unit == null) parts[i].unit = parts[i + 1]?.unit ?? null;
  }
  const [a, b = a] = parts.slice(0, 2).map((p) => Math.round(p.n * (p.unit ?? 1)));
  return [Math.min(a, b), Math.max(a, b)];
};

// "9-18", "9am to 6pm", "8 to 8" -> [start, end] hours. "Any time" is 0 and 0.
const parseHours = (text) => {
  const t = text.toLowerCase();
  if (/any ?time|all day|24 ?h|round the clock/.test(t)) return [0, 0];
  const parts = [...t.matchAll(/(\d{1,2})(?::\d{2})?\s*(am|pm)?/g)].map((m) => {
    let h = Number(m[1]);
    if (m[2] === 'pm' && h < 12) h += 12;
    if (m[2] === 'am' && h === 12) h = 0;
    return { h, explicit: !!m[2] };
  });
  if (parts.length < 2) return null;
  const s = parts[0].h;
  let e = parts[1].h;
  // "9 to 6" means six in the evening.
  if (e <= s && !parts[1].explicit && e < 12) e += 12;
  if (s > 23 || e > 24 || e <= s) return null;
  return [s, e];
};

const validZone = (tz) => {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};
const browserZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

const pickByText = (list, text, toText) => {
  const t = text.trim().toLowerCase();
  if (!t) return [];
  const exact = list.filter((x) => toText(x).some((s) => s && s.toLowerCase() === t));
  if (exact.length) return exact;
  return list.filter((x) => toText(x).some((s) => s && s.toLowerCase().includes(t)));
};

// ── saying values back ──────────────────────────────────────────────────────

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const fmtMinutes = (n) => {
  if (n === 0) return '0 minutes';
  if (n % 60 === 0) return plural(n / 60, 'hour');
  if (n > 60) return `${Math.floor(n / 60)} h ${n % 60} min`;
  return plural(n, 'minute');
};
const rangeLabel = (a, b) => {
  if (a === 0 && b === 0) return 'Straight away';
  if (a === b) return fmtMinutes(a);
  // "5 to 30 minutes", "1 to 3 hours"; mixed units say both.
  if (b < 60) return `${a} to ${b} minutes`;
  if (a > 0 && a % 60 === 0 && b % 60 === 0) return `${a / 60} to ${b / 60} hours`;
  return `${fmtMinutes(a)} to ${fmtMinutes(b)}`;
};
const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;
const windowLabel = (s, e, weekdays) => `${Number(s) === Number(e) ? 'Any time' : `${hourLabel(s)} to ${hourLabel(e)}`}, ${weekdays ? 'weekdays' : 'every day'}`;
const followupLabel = (days) => {
  if (!days.length) return 'No follow-ups';
  let total = 0;
  const on = days.map((d) => (total += d));
  return `${plural(days.length, 'follow-up')}, on day ${on.join(', ')}`;
};

// "https://www.site.com/page, other.com" -> ['site.com', 'other.com'], as the server keeps them.
const domainsIn = (text) => [...new Set(String(text).toLowerCase().split(/[\s,;]+/)
  .map((s) => s.replace(/^[a-z]+:\/\//, '').replace(/^www\./, '').split(/[/?#:]/)[0])
  .filter((d) => /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(d)))].slice(0, 500);

// Country names and codes in a typed answer: "US, France and nl" -> ['US', 'FR', 'NL'], and what was not a country.
const COUNTRY_ALIAS = { uk: 'GB', 'great britain': 'GB', england: 'GB', usa: 'US', america: 'US', 'united states of america': 'US', holland: 'NL' };
let regionNames;
const countryLabel = (code) => {
  try {
    if (!regionNames) regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
};
const countriesIn = (text, markets) => {
  const codes = [];
  const unknown = [];
  for (const part of String(text).split(/[,;\n/]+|\s+and\s+|\s*&\s*/i).map((s) => s.trim().replace(/[.!]+$/, '')).filter(Boolean)) {
    const lower = part.toLowerCase();
    const hit = COUNTRY_ALIAS[lower]
      || markets.find((m) => m.code.toLowerCase() === lower || String(m.name).toLowerCase() === lower)?.code
      || (/^[a-z]{2}$/i.test(part) && countryLabel(part.toUpperCase()) !== part.toUpperCase() ? part.toUpperCase() : null);
    if (hit) codes.push(hit);
    else unknown.push(part);
  }
  return { codes: [...new Set(codes)], unknown };
};

// One brand fact affiliates ask about, kept in form.brand_facts[key]; skipped, a colleague answers it.
const factStep = (key, title, ask, hint, placeholder, presets = []) => {
  const skip = 'Skip: a colleague answers it';
  const set = (form, value) => ({ brand_facts: { ...(form.brand_facts || {}), [key]: value } });
  return {
    key: `fact_${key}`,
    title,
    ask,
    hint,
    placeholder,
    multiline: true,
    options: ({ form }) => [
      ...presets.map((p) => ({ label: p, patch: set(form, p) })),
      { label: skip, patch: set(form, '') }
    ],
    parse: (text, { form }) => (SKIP.test(text.trim())
      ? { label: skip, patch: set(form, '') }
      : { patch: set(form, text.trim().slice(0, 500)) })
  };
};

// Kinds of affiliate offered as answers to "what suits the brand best".
const FIT_KINDS = ['Casino review and bonus comparison sites', 'Sports betting tips and odds sites', 'News and media sites with a gambling section'];

const applySigner = (form, name, title) => ({
  senders: form.senders.map((s) => ({ ...s, name, title })),
  sender_name: name,
  sender_title: title
});

// ── the questions ───────────────────────────────────────────────────────────
//
// Each question either offers `options` (click one, or type something that
// matches one), or is `multi` (pick several, then Done), and may `parse` a
// typed answer of its own. `parse` returns null for "not mine", so a typed
// option label still lands on the option. `ack` is what the assistant says
// back once it has the answer, when there is something worth saying.

const STEPS = [
  {
    key: 'brand',
    title: 'Brand',
    ask: ({ brands }) => (brands.length
      ? 'Which brand will this team work for?'
      : 'Which brand will this team work for? You have no brands in MARS yet: add one on the dashboard first, then come back here.'),
    hint: 'Any brand you added in MARS: on the dashboard, in competitor spy or in affiliate gap. Pick it from the list, or type to search.',
    placeholder: 'Or type a brand name, e.g. Swift Casino',
    searchable: true,
    options: ({ brands }) => brands.map((b) => ({
      label: b.brand_name,
      sub: b.brand_url,
      match: [b.brand_name, b.brand_url],
      patch: { brand_id: b.id }
    })),
    retry: 'I could not find a brand by that name. Try part of its name or its domain.',
    ack: (form, { brands, marketName }) => {
      const b = brands.find((x) => String(x.id) === String(form.brand_id));
      if (!b) return null;
      const markets = (b.markets || []).map(marketName);
      const verticals = b.verticals || [];
      const gaps = [!markets.length && 'no countries', !verticals.length && 'no iGaming vertical'].filter(Boolean);
      if (gaps.length) {
        return `Noted. One thing: ${b.brand_name} has ${gaps.join(' and ')} in MARS yet. You can create the team now, but it cannot start until that is set on the brand.`;
      }
      return `${b.brand_name} it is: ${verticals.join(', ')} in ${markets.join(', ')}. The team takes both from the brand.`;
    }
  },
  {
    key: 'name',
    title: 'Team name',
    ask: () => 'What should we call the team?',
    hint: 'Only you see it: it names the team in the picker at the top.',
    placeholder: 'e.g. Swift Casino UK',
    options: ({ brand }) => (brand ? [{ label: `${brand.brand_name} team`, patch: { name: `${brand.brand_name} team` }, recommended: true }] : []),
    parse: (text) => {
      const name = text.trim().slice(0, 120);
      return name ? { patch: { name } } : { error: 'The team needs a name.' };
    }
  },
  {
    key: 'brand_pitch',
    title: 'Why list the brand',
    ask: ({ brand }) => `Why should an affiliate list ${brand?.brand_name || 'the brand'}?`,
    hint: 'What sets it apart: the welcome offer, how well it converts, anything true and specific. Payments, withdrawals and games come next, one at a time.',
    placeholder: 'e.g. 100% up to £200 welcome bonus, payouts within 24 hours, 3,000+ slots...',
    multiline: true,
    options: () => [{ label: 'Skip for now', patch: { brand_pitch: '' } }],
    parse: (text) => (SKIP.test(text.trim())
      ? { label: 'Skip for now', patch: { brand_pitch: '' } }
      : { patch: { brand_pitch: text.trim().slice(0, 2000) } }),
    ack: (form) => (form.brand_pitch ? null : 'Fine. Without it the agents keep to what MARS knows about the brand; you can add it in Setup any time.')
  },
  // What affiliates ask about the brand before they list it. Answered here, the agents answer them; skipped, a colleague does.
  factStep('payments', 'Payment methods',
    ({ brand }) => `Which payment methods can players use at ${brand?.brand_name || 'the brand'}?`,
    'Affiliates ask, because their readers do. List the main ones, e.g. "Visa, Mastercard, PayPal, Trustly, Apple Pay".',
    'e.g. Visa, Mastercard, PayPal, Trustly, Apple Pay'),
  factStep('withdrawals', 'Withdrawals',
    () => 'How fast are withdrawals paid, and how does verification (KYC) work?',
    'The question affiliates ask most after the deal itself. Only what is true for most players.',
    'e.g. E-wallets within 24 hours, cards 1 to 3 days; KYC at the first withdrawal',
    ['E-wallets within 24 hours, cards and bank 1 to 3 days', 'Within 24 hours for all methods']),
  factStep('games', 'Games and betting',
    ({ brand }) => `What do players get to play or bet on at ${brand?.brand_name || 'the brand'}?`,
    'Game count and providers, live casino, sportsbook coverage: what a review page would list.',
    'e.g. 3,000+ slots from Pragmatic Play and NetEnt, Evolution live casino, sportsbook with 30 sports'),
  factStep('app_support', 'App and support',
    () => 'Is there a mobile app, and what support do players get?',
    'App or mobile site, live chat or email, hours and languages.',
    'e.g. iOS and Android apps, 24/7 live chat in English and Swedish',
    ['Mobile site only, 24/7 live chat', 'iOS and Android apps, 24/7 live chat']),
  factStep('creatives', 'Marketing material',
    () => 'What marketing material can affiliates get?',
    'Banners, logos, landing pages, exclusive bonus codes: what the agents may promise once a deal is agreed.',
    'e.g. Banners, logos and landing pages in the partner portal',
    ['Banners, logos and landing pages in the partner portal', 'Banners and logos on request']),
  {
    key: 'brand_rules',
    title: 'Never say',
    ask: ({ brand }) => `Is there anything the agents must never say or promise about ${brand?.brand_name || 'the brand'}?`,
    hint: 'The brand\'s own rules, kept in every email whatever the affiliate says: e.g. never call a bonus risk-free or free money, no word about crypto payments, never name the sister brand.',
    placeholder: 'e.g. Never call a bonus risk-free. Do not mention crypto payments.',
    multiline: true,
    options: () => [{ label: 'Nothing beyond the usual rules', patch: { brand_rules: '' }, recommended: true }],
    parse: (text) => (SKIP.test(text.trim())
      ? { label: 'Nothing beyond the usual rules', patch: { brand_rules: '' } }
      : { patch: { brand_rules: text.trim().slice(0, 1500) } }),
    ack: (form) => (form.brand_rules ? 'Got it: every agent gets that as a rule it never breaks.' : null)
  },
  {
    key: 'restricted_geos',
    title: 'Restricted countries',
    ask: () => 'Are there countries the brand must not take players from?',
    hint: 'Countries the licence or the terms rule out, e.g. "US, France". The Scout skips sites whose visitors come mostly from there, and the agents never ask for players from them.',
    placeholder: 'e.g. United States, France, NL',
    options: () => [{ label: 'None beyond the brand\'s markets', patch: { restricted_geos: [] }, recommended: true }],
    parse: (text, { meta }) => {
      if (SKIP.test(text.trim())) return { label: 'None beyond the brand\'s markets', patch: { restricted_geos: [] } };
      const { codes, unknown } = countriesIn(text, meta.markets || []);
      if (unknown.length) return { error: `I do not know ${unknown.map((u) => `"${u}"`).join(', ')}. Use country names or two-letter codes, e.g. France or FR.` };
      if (!codes.length) return null;
      return { label: codes.map(countryLabel).join(', '), patch: { restricted_geos: codes } };
    }
  },
  {
    key: 'program_name',
    title: 'Programme name',
    ask: () => 'What is the affiliate programme called?',
    hint: 'The name affiliates know it by, e.g. BrandX Partners.',
    placeholder: 'e.g. Swift Partners',
    options: () => [{ label: 'Skip for now', patch: { program_name: '' } }],
    parse: (text) => (SKIP.test(text.trim())
      ? { label: 'Skip for now', patch: { program_name: '' } }
      : { patch: { program_name: text.trim().slice(0, 120) } })
  },
  {
    key: 'program_signup_url',
    title: 'Sign-up link',
    ask: () => 'Where do affiliates sign up for the programme?',
    hint: 'The link is sent once a deal is agreed.',
    placeholder: 'https://partners.brand.com/signup',
    options: () => [{ label: 'Skip for now', patch: { program_signup_url: '' } }],
    parse: (text) => {
      const t = text.trim();
      if (SKIP.test(t)) return { label: 'Skip for now', patch: { program_signup_url: '' } };
      if (/^https?:\/\/\S+$/i.test(t)) return { patch: { program_signup_url: t.slice(0, 500) } };
      if (/^[^\s/]+\.[^\s]+$/.test(t)) return { label: `https://${t}`, patch: { program_signup_url: `https://${t}`.slice(0, 500) } };
      return { error: 'That does not look like a link. Paste the full address, e.g. https://partners.brand.com/signup, or say skip.' };
    }
  },
  {
    key: 'offer_ids',
    title: 'Deals',
    multi: true,
    ask: ({ deals }) => (deals.length
      ? 'Which saved deals may the team offer?'
      : 'Which deal should the team offer? You have no saved deal yet, so let\'s make one here.'),
    hint: ({ deals }) => (deals.length
      ? 'Pick one or more. The first one you pick is the default; each affiliate gets the deal for its market and keeps it. Need another? Make it here with the deal desk.'
      : 'The agents only offer terms you saved. Tell the deal desk the deal in your own words: it asks for anything missing, and once you say yes it is saved and picked for this team. Or skip and pick one in Setup later.'),
    items: ({ deals }) => deals.map((d) => ({
      id: d.id,
      label: d.name || 'Untitled deal',
      sub: [d.opening_line, (d.geos || []).join(', ') || 'All markets'].filter(Boolean).join(' · ')
    })),
    initial: ({ form }) => form.offer_ids,
    finish: (ids, { deals }) => ({
      patch: { offer_ids: ids },
      label: ids.length ? ids.map((id) => deals.find((d) => d.id === id)?.name || 'Untitled deal').join(', ') : 'None for now'
    }),
    ack: (form) => (form.offer_ids.length ? null : 'Fine. The team can be created without one, but it will not start until it has a deal.')
  },
  {
    key: 'senders',
    title: 'Mailboxes',
    multi: true,
    ask: ({ mailboxes }) => (mailboxes.length
      ? 'Which mailbox should the emails come from?'
      : 'Which mailbox should the emails come from? None is connected yet.'),
    hint: ({ mailboxes }) => (mailboxes.length
      ? 'The team sends from these and reads the replies there, so each needs IMAP. A new affiliate gets the least busy one and keeps it for the whole thread.'
      : 'Connect one in Email Settings, then pick it in Setup. You can skip it for now.'),
    items: ({ mailboxes }) => mailboxes.map((m) => ({
      id: m.id,
      label: m.email,
      sub: !m.can_read ? 'No IMAP: replies cannot be read' : m.is_verified ? null : 'Not verified',
      disabled: !m.can_read
    })),
    initial: ({ form }) => form.senders.map((s) => s.mailbox_id),
    finish: (ids, { mailboxes, form }) => ({
      patch: {
        senders: ids.map((id) => {
          const box = mailboxes.find((m) => m.id === id);
          return form.senders.find((s) => s.mailbox_id === id)
            || { mailbox_id: id, name: form.sender_name || box?.display_name || '', title: form.sender_title || 'Affiliate Manager' };
        })
      },
      label: ids.length ? ids.map((id) => mailboxes.find((m) => m.id === id)?.email).filter(Boolean).join(', ') : 'None for now'
    })
  },
  {
    key: 'signer',
    title: 'Signed by',
    ask: () => 'Whose name and title should the emails be signed with?',
    hint: 'Name, then title, e.g. "Sam Lee, Affiliate Manager". Every mailbox on the team signs this way; you can give each its own in Setup.',
    placeholder: 'Sam Lee, Affiliate Manager',
    options: ({ form }) => {
      const name = form.senders[0]?.name || form.sender_name;
      return name ? [{ label: `${name}, Affiliate Manager`, patch: applySigner(form, name, 'Affiliate Manager'), recommended: true }] : [];
    },
    parse: (text, { form }) => {
      const [name, ...rest] = text.split(/\s*(?:,|\||\s-\s)\s*/);
      if (!name || !name.trim()) return { error: 'Give the name the emails are signed with.' };
      const title = rest.join(', ').trim() || 'Affiliate Manager';
      return { label: `${name.trim()}, ${title}`, patch: applySigner(form, name.trim().slice(0, 120), title.slice(0, 120)) };
    }
  },
  {
    key: 'sign_off',
    title: 'Sign-off',
    ask: () => 'How should the emails close, just above the name?',
    hint: 'Click one or type your own.',
    placeholder: 'e.g. Warm regards,',
    options: () => ['Best regards,', 'Kind regards,', 'Warm regards,', 'Many thanks,'].map((s) => ({ label: s, patch: { sign_off: s }, recommended: s === 'Best regards,' })),
    parse: (text) => {
      const s = text.trim().slice(0, 60);
      return s ? { patch: { sign_off: s } } : null;
    }
  },
  // Optional links and logos under the signature, for every mailbox (each can have its own in Setup).
  {
    key: 'signature_links',
    title: 'Signature links',
    ask: () => 'Should Teams, Telegram or LinkedIn go under the name and designation? Each one is optional.',
    hint: 'Type any of them in one go, e.g. "Telegram @sam_lee, LinkedIn linkedin.com/in/sam-lee, Teams sam@brandx.com". Each shows as a link.',
    placeholder: 'Telegram @sam_lee, linkedin.com/in/sam-lee',
    options: ({ form }) => [{ label: 'No links', patch: { signature_extras: { ...form.signature_extras, teams: '', telegram: '', linkedin: '' } }, recommended: true }],
    parse: (text, { form }) => {
      if (SKIP.test(text.trim())) return { label: 'No links', patch: { signature_extras: { ...form.signature_extras, teams: '', telegram: '', linkedin: '' } } };
      const { found, bad } = parseContacts(text);
      if (bad.length) return { error: `I could not read "${bad[0]}". ${CONTACT_FIELDS.map((f) => `${f.label}: ${f.help}`).join(' ')}` };
      if (!Object.keys(found).length) return null;
      const extras = { teams: '', telegram: '', linkedin: '', ...found };
      return {
        label: CONTACT_FIELDS.filter((f) => extras[f.key]).map((f) => `${f.label}: ${extras[f.key]}`).join(', '),
        patch: { signature_extras: { ...form.signature_extras, ...extras } }
      };
    }
  },
  // Logos are added one by one in the editor under the question (uploaded, or an image link typed here),
  // each with the page it opens; Done answers it. Several logos, each with its own link (the user, 30 Oct 2026).
  {
    key: 'signature_logo',
    title: 'Footer logos',
    ask: () => 'Do you want logos at the bottom of the signature? Each one can open a link when clicked.',
    hint: `Upload PNG, JPG or GIF files under 1 MB, or type an image link, with the page it opens after it, e.g. "https://brandx.com/logo.png brandx.com". Add up to ${MAX_LOGOS}, then press Done.`,
    placeholder: 'Image link, then the page it opens (optional)',
    upload: true,
    options: ({ form }) => {
      const logos = cleanLogos(logosOf(form.signature_extras));
      const none = { label: 'No logos', patch: { signature_extras: { ...form.signature_extras, logos: [] } } };
      // A logo the editor marks as wrong is fixed or removed there before Done is offered.
      if (!logos.length || logosIssue(logosOf(form.signature_extras))) return [{ ...none, recommended: !logos.length }];
      const done = { label: `Done: ${logos.length} logo${logos.length === 1 ? '' : 's'}`, match: ['done', 'ok', 'that is all'], patch: { signature_extras: { ...form.signature_extras, logos } }, recommended: true };
      return [done, none];
    },
    parse: (text, { form }) => {
      const v = text.trim();
      if (SKIP.test(v)) return { label: 'No logos', patch: { signature_extras: { ...form.signature_extras, logos: [] } } };
      // "image [page]": a logo added to the list; the question stays until Done.
      const [src, href = '', ...more] = v.split(/\s*(?:->|→|,|\s)\s*/).filter(Boolean);
      if (!/^https?:\/\//i.test(src || '') || more.length) return null;
      const list = logosOf(form.signature_extras);
      const issue = logosIssue([...list, { src, href }]);
      if (issue) return { error: issue };
      return { stay: true, note: 'Added. Add another, or press Done.', patch: { signature_extras: { ...form.signature_extras, logos: [...list, { src: src.slice(0, 1000), href: href.slice(0, 1000) }] } } };
    }
  },
  {
    key: 'followup_days',
    title: 'Follow-ups',
    ask: () => 'If an affiliate does not reply, how many follow-ups should go, and how many days apart?',
    hint: 'Each one goes in the same thread, counted from the email before it, and they stop the moment the affiliate replies. Type the gaps in days, e.g. "2, 3, 5".',
    placeholder: 'e.g. 2, 3, 5',
    options: () => [
      { label: 'Two: after 2 days, then 3 more', patch: { followup_days: [2, 3] }, recommended: true },
      { label: 'Three: after 3, 4 and 7 days', patch: { followup_days: [3, 4, 7] } },
      { label: 'One, after 4 days', patch: { followup_days: [4] } },
      { label: 'None', patch: { followup_days: [] } }
    ],
    parse: (text) => {
      const t = text.toLowerCase();
      if (/^(none|no|zero|0)\b/.test(t.trim())) return { label: 'None', patch: { followup_days: [] } };
      let days;
      const count = t.match(/(\d+)\s*follow/);
      if (count) {
        const every = t.match(/(\d+)\s*days?\s*apart|every\s*(\d+)/);
        days = Array(Number(count[1])).fill(Number(every ? every[1] || every[2] : 3));
      } else {
        days = (t.match(/\d+/g) || []).map(Number);
      }
      if (!days.length) return null;
      if (days.length > 6) return { error: 'At most 6 follow-ups.' };
      if (days.some((d) => d < 1 || d > 30)) return { error: 'Each gap has to be between 1 and 30 days.' };
      return { label: followupLabel(days), patch: { followup_days: days } };
    },
    ack: (form) => (form.followup_days.length
      ? `So follow-ups land on ${followupLabel(form.followup_days).split(', on ')[1]}, counted from the first email.`
      : 'Then an affiliate who does not answer the first email is marked no reply.')
  },
  {
    key: 'outreach_delay',
    title: 'Wait before each email',
    ask: () => 'How long should the team wait before sending each email?',
    hint: 'A random wait in the range you give, so first emails and follow-ups go out spread apart instead of all at once. Always inside the sending hours. Type a range, e.g. "5 to 30 minutes".',
    placeholder: 'e.g. 5 to 30 minutes',
    options: () => [
      { label: '1 to 15 minutes', patch: { outreach_delay_min_minutes: 1, outreach_delay_max_minutes: 15 }, recommended: true },
      { label: '15 to 60 minutes', patch: { outreach_delay_min_minutes: 15, outreach_delay_max_minutes: 60 } },
      { label: '1 to 3 hours', patch: { outreach_delay_min_minutes: 60, outreach_delay_max_minutes: 180 } },
      { label: 'Straight away', patch: { outreach_delay_min_minutes: 0, outreach_delay_max_minutes: 0 } }
    ],
    parse: (text) => {
      const r = parseMinutes(text);
      if (!r) return null;
      if (r[1] > 1440) return { error: 'At most 24 hours (1,440 minutes).' };
      return { label: rangeLabel(r[0], r[1]), patch: { outreach_delay_min_minutes: r[0], outreach_delay_max_minutes: r[1] } };
    }
  },
  {
    key: 'reply_delay',
    title: 'Reply after',
    ask: () => 'When an affiliate replies, how long should the team wait before answering?',
    hint: 'A random wait in this range, so answers never look instant.',
    placeholder: 'e.g. 30 minutes to 2 hours',
    options: () => [
      { label: '30 minutes to 2 hours', patch: { reply_delay_min_minutes: 30, reply_delay_max_minutes: 120 }, recommended: true },
      { label: '5 to 30 minutes', patch: { reply_delay_min_minutes: 5, reply_delay_max_minutes: 30 } },
      { label: '2 to 6 hours', patch: { reply_delay_min_minutes: 120, reply_delay_max_minutes: 360 } }
    ],
    parse: (text) => {
      const r = parseMinutes(text);
      if (!r) return null;
      if (r[1] > 2880) return { error: 'At most 48 hours (2,880 minutes).' };
      return { label: rangeLabel(r[0], r[1]), patch: { reply_delay_min_minutes: r[0], reply_delay_max_minutes: r[1] } };
    }
  },
  {
    key: 'send_window',
    title: 'Sending hours',
    ask: () => 'Which hours may emails go out, and on which days?',
    hint: 'In the time zone you pick next. E.g. "8 to 20, weekdays" or "any time".',
    placeholder: 'e.g. 9am to 6pm, weekdays',
    options: () => [
      { label: '09:00 to 18:00, weekdays', patch: { send_window_start: 9, send_window_end: 18, weekdays_only: true }, recommended: true },
      { label: '09:00 to 18:00, every day', patch: { send_window_start: 9, send_window_end: 18, weekdays_only: false } },
      { label: '08:00 to 20:00, weekdays', patch: { send_window_start: 8, send_window_end: 20, weekdays_only: true } },
      { label: 'Any time, every day', patch: { send_window_start: 0, send_window_end: 0, weekdays_only: false } }
    ],
    parse: (text, { form }) => {
      const t = text.toLowerCase();
      const hours = parseHours(t);
      const weekdays = /weekday|mon\w*\s*(?:-|to)\s*fri|working days|business days/.test(t) ? true
        : /every ?day|daily|weekend|all week|7 days/.test(t) ? false : null;
      if (!hours && weekdays == null) return null;
      const patch = {
        ...(hours ? { send_window_start: hours[0], send_window_end: hours[1] } : {}),
        ...(weekdays != null ? { weekdays_only: weekdays } : {})
      };
      const next = { ...form, ...patch };
      return { label: windowLabel(next.send_window_start, next.send_window_end, next.weekdays_only), patch };
    }
  },
  {
    key: 'timezone',
    title: 'Time zone',
    ask: () => 'Which time zone are those hours in?',
    hint: 'Usually the one your affiliates are in. Type a city or a zone, e.g. Europe/Malta.',
    placeholder: 'e.g. Europe/London',
    options: () => {
      const mine = browserZone();
      const zones = [...new Set([mine && validZone(mine) ? mine : null, ...TIMEZONES].filter(Boolean))];
      return zones.map((tz) => ({
        label: tz,
        match: [tz, tz.split('/').pop().replace(/_/g, ' ')],
        patch: { timezone: tz },
        recommended: tz === mine
      }));
    },
    parse: (text) => {
      const t = text.trim();
      return t.includes('/') && validZone(t) ? { patch: { timezone: t } } : null;
    },
    retry: 'I do not know that time zone. Try a city like London or Malta, or a zone like Europe/Berlin.'
  },
  {
    key: 'daily_new_contacts',
    title: 'New affiliates a day',
    ask: () => 'How many new affiliates should the team email per day?',
    hint: 'First emails only; follow-ups and replies come on top. Start modest: a mailbox sending hundreds of cold emails a day ends up in spam.',
    placeholder: 'e.g. 20',
    options: () => [10, 15, 25, 50].map((n) => ({ label: `${n} a day`, patch: { daily_new_contacts: n }, recommended: n === 15 })),
    parse: (text) => {
      const n = firstNumber(text);
      if (n == null) return null;
      if (n < 1 || n > 200) return { error: 'Somewhere between 1 and 200 a day.' };
      return { label: `${n} a day`, patch: { daily_new_contacts: n } };
    }
  },
  {
    key: 'auto_scout',
    title: 'Scouts on its own',
    ask: () => 'Should the team look for new affiliates on its own?',
    hint: 'It searches every 6 hours. Say no and it only emails the affiliates you hand over, plus the ones you find with Scout now.',
    options: () => [
      { label: 'Yes, scout on its own', patch: { auto_scout: true }, recommended: true },
      { label: 'No, only the ones I hand over', patch: { auto_scout: false } }
    ],
    parse: (text) => {
      const v = yesNo(text);
      if (v == null) return null;
      return { label: v ? 'Yes, scout on its own' : 'No, only the ones I hand over', patch: { auto_scout: v } };
    }
  },
  {
    key: 'min_organic_traffic',
    title: 'Least traffic',
    ask: () => 'What is the least organic traffic an affiliate site should have?',
    hint: 'Monthly visits from search, as MARS sees them. Smaller sites are left out.',
    placeholder: 'e.g. 2,000 or 5k',
    options: () => [
      { label: 'Any size', patch: { min_organic_traffic: 0 } },
      { label: '500 a month', patch: { min_organic_traffic: 500 }, recommended: true },
      { label: '5,000 a month', patch: { min_organic_traffic: 5000 } },
      { label: '20,000 a month', patch: { min_organic_traffic: 20000 } }
    ],
    parse: (text) => {
      if (/^any\b/i.test(text.trim())) return { label: 'Any size', patch: { min_organic_traffic: 0 } };
      const n = firstNumber(text);
      if (n == null) return null;
      if (n > 50000000) return { error: 'That is more traffic than any affiliate has. Try a smaller number.' };
      return { label: `${n.toLocaleString()} a month`, patch: { min_organic_traffic: n } };
    }
  },
  {
    key: 'licence_preference',
    title: 'Goes after',
    ask: () => 'Which affiliates should it go after?',
    hint: 'By the kind of brands they already list.',
    options: () => [
      { label: 'Any affiliates', match: ['any', 'all'], patch: { licence_preference: 'any' }, recommended: true },
      { label: 'Ones that list nationally licensed brands', match: ['licensed', 'licenced', 'national', 'regulated'], patch: { licence_preference: 'licensed' } },
      { label: 'Ones that list offshore brands', match: ['offshore', 'curacao', 'unlicensed'], patch: { licence_preference: 'offshore' } }
    ]
  },
  {
    key: 'ideal_affiliates',
    title: 'Best-fit affiliates',
    ask: ({ brand }) => `What kind of affiliate suits ${brand?.brand_name || 'the brand'} best?`,
    hint: 'The Scout and the Vetter prefer these when they choose; markets and verticals already come from the brand. Click one, or describe it in your own words.',
    placeholder: 'e.g. Casino review sites and bonus comparison sites with UK traffic',
    multiline: true,
    options: () => [
      ...FIT_KINDS.map((k) => ({ label: k, patch: { ideal_affiliates: k } })),
      { label: 'Any site that fits the markets', patch: { ideal_affiliates: '' }, recommended: true }
    ],
    parse: (text) => {
      const t = text.trim();
      if (SKIP.test(t) || /^any\b/i.test(t)) return { label: 'Any site that fits the markets', patch: { ideal_affiliates: '' } };
      return { patch: { ideal_affiliates: t.slice(0, 1000) } };
    }
  },
  {
    key: 'placement_goal',
    title: 'What we want back',
    ask: () => 'What do you want from an affiliate in return for the deal?',
    hint: 'Where the brand should appear on their site. The Affiliate Manager asks for it when the deal is agreed or position comes up.',
    placeholder: 'e.g. A top-3 spot in their UK casino list and a full review page',
    multiline: true,
    options: () => [
      { label: 'A top-3 spot in their main list', patch: { placement_goal: 'A top-3 spot in their main list' } },
      { label: 'A full review page and a spot in their lists', patch: { placement_goal: 'A full review page and a spot in their lists' }, recommended: true },
      { label: 'Any spot in their lists to start', patch: { placement_goal: 'Any spot in their lists to start, moving up as it converts' } }
    ],
    parse: (text) => {
      const t = text.trim();
      return t && !SKIP.test(t) ? { patch: { placement_goal: t.slice(0, 600) } } : { label: 'Any spot in their lists to start', patch: { placement_goal: 'Any spot in their lists to start, moving up as it converts' } };
    }
  },
  {
    key: 'exclude_domains',
    title: 'Never contact',
    ask: () => 'Are there sites the team must never contact?',
    hint: 'Current partners, a rival\'s own review sites, anyone you have fallen out with. Type the sites, one per line or with commas. The Scout skips them, and they cannot be handed over.',
    placeholder: 'partner-site.com\nrival-reviews.com',
    multiline: true,
    options: () => [{ label: 'None', patch: { exclude_domains: [] }, recommended: true }],
    parse: (text) => {
      if (SKIP.test(text.trim())) return { label: 'None', patch: { exclude_domains: [] } };
      const domains = domainsIn(text);
      if (!domains.length) return { error: 'I could not find a site in that. Type domains like partner-site.com, or say none.' };
      return { label: domains.join(', '), patch: { exclude_domains: domains } };
    },
    ack: (form) => (form.exclude_domains.length ? `${plural(form.exclude_domains.length, 'site')} on the never-contact list.` : null)
  },
  {
    key: 'listing_fee_ok',
    title: 'Listing fees',
    ask: () => 'If an affiliate asks for a listing fee, are you ready to pay one?',
    hint: 'Some sites charge a monthly fee for a spot on their page, whatever it sends. The agents never bring a fee up: this is only for when the affiliate does.',
    options: () => [
      { label: 'No, performance deals only', match: ['no', 'never', 'performance'], patch: { listing_fee_ok: false }, recommended: true },
      { label: 'Yes, if the site is worth it', match: ['yes', 'pay', 'sometimes'], patch: { listing_fee_ok: true } }
    ],
    parse: (text) => {
      const v = yesNo(text);
      if (v == null) return null;
      return { label: v ? 'Yes, if the site is worth it' : 'No, performance deals only', patch: { listing_fee_ok: v } };
    },
    ack: (form) => (form.listing_fee_ok ? null : 'Then a request for a fee gets a plain, friendly no, and the agents offer the performance deal instead.')
  },
  {
    key: 'listing_fee_range',
    title: 'Fee range',
    when: (form) => form.listing_fee_ok === true,
    ask: ({ cur }) => `What is the least and the most you would pay for a listing, a month${cur ? `, in ${cur}` : ''}?`,
    hint: 'The agent opens near the least and never goes above the most; past it, a person decides. E.g. "500 to 2,000" or "1k-3k".',
    placeholder: 'e.g. 500 to 2,000',
    options: ({ sym }) => [[250, 1000], [500, 2000], [1000, 5000]].map(([a, b]) => ({
      label: `${sym}${a.toLocaleString()} to ${sym}${b.toLocaleString()}`,
      patch: { listing_fee_min: a, listing_fee_max: b }
    })),
    parse: (text, { sym }) => {
      const amounts = amountsIn(text);
      if (!amounts.length) return null;
      if (amounts.some((v) => v > 10000000)) return { error: 'That is more than any listing is worth. Try a smaller number.' };
      // "Up to 2,000": the most alone, and the agent opens at half of it.
      const [lo, hi] = amounts.length === 1 ? [null, amounts[0]] : [Math.min(amounts[0], amounts[1]), Math.max(amounts[0], amounts[1])];
      if (!(hi > 0)) return { error: 'The most has to be more than nothing.' };
      return {
        label: lo == null ? `Up to ${sym}${hi.toLocaleString()} a month` : `${sym}${lo.toLocaleString()} to ${sym}${hi.toLocaleString()} a month`,
        patch: { listing_fee_min: lo ?? '', listing_fee_max: hi }
      };
    }
  },
  {
    key: 'listing_fee_firmness',
    title: 'How hard on fees',
    when: (form) => form.listing_fee_ok === true,
    ask: () => 'How hard should the agent bargain on the fee?',
    hint: 'Whatever you pick, Oddschecker, AskGamblers, Betfair and other big names get the soft approach: they earn their fee.',
    options: () => [
      { label: 'Low: agree to their fee', sub: 'Their fee, up to your most, is a straight yes.', match: ['low', 'easy', 'soft', 'agree'], patch: { listing_fee_firmness: 'low' } },
      { label: 'Medium: bargain politely', sub: 'Opens at your least, climbs in steps, trades every step for something.', match: ['medium', 'mid', 'normal', 'polite'], patch: { listing_fee_firmness: 'medium' }, recommended: true },
      { label: 'Max: bargain hard', sub: 'Performance deal first, then small steps, two holds before each.', match: ['max', 'hard', 'high', 'tough', 'strong'], patch: { listing_fee_firmness: 'max' } }
    ]
  },
  {
    key: 'cooloff_days',
    title: 'Stop agent',
    ask: () => 'When an affiliate won\'t meet you in the middle on terms, how long should the Stop agent step back before checking in?',
    hint: 'It puts the lead on hold instead of closing it, then writes once more in the same thread: no pressure, never a better offer. The On hold tab shows every lead it holds, why, and when it will write.',
    placeholder: 'e.g. 4 days',
    options: () => [
      { label: '2 days', patch: { cooloff_days: 2 }, recommended: true },
      { label: '3 days', patch: { cooloff_days: 3 } },
      { label: '5 days', patch: { cooloff_days: 5 } },
      { label: 'A week', patch: { cooloff_days: 7 } },
      { label: 'Don\'t check back: close the deal', match: ['never', 'don\'t', 'off', 'close', 'no'], patch: { cooloff_days: 0 } }
    ],
    parse: (text) => {
      const t = text.toLowerCase();
      if (/^(never|no|off|none|0|don'?t|do not)\b/.test(t.trim())) return { label: 'Don\'t check back: close the deal', patch: { cooloff_days: 0 } };
      const num = firstNumber(t);
      const weeks = /\bweeks?\b/.test(t);
      const days = weeks ? (num ?? 1) * 7 : num;
      if (days == null) return /\bweek\b/.test(t) ? { label: 'A week', patch: { cooloff_days: 7 } } : null;
      if (days < 1 || days > 30) return { error: 'Somewhere between 1 and 30 days.' };
      return { label: days === 7 ? 'A week' : `${days} day${days === 1 ? '' : 's'}`, patch: { cooloff_days: days } };
    },
    ack: (form) => (Number(form.cooloff_days) > 0 ? null : 'Then a no on terms closes the deal straight away, as before.')
  },
  {
    key: 'cooloff_checkins',
    title: 'Check-ins',
    when: (form) => Number(form.cooloff_days) > 0,
    ask: () => 'How many times should it check back before letting go?',
    hint: 'A reply at any point picks the negotiation up again. Silence after the last check-in closes it as lost.',
    options: () => [
      { label: 'Once', match: ['once', '1', 'one'], patch: { cooloff_checkins: 1 }, recommended: true },
      { label: 'Twice', match: ['twice', '2', 'two'], patch: { cooloff_checkins: 2 } },
      { label: 'Three times', match: ['three', '3', 'thrice'], patch: { cooloff_checkins: 3 } }
    ]
  },
  {
    key: 'approval_mode',
    title: 'Needs your approval',
    ask: () => 'What should wait for your approval before it is sent?',
    hint: 'Whatever you pick, a draft that breaks a rule or goes over a deal limit always waits for a person.',
    options: ({ meta }) => (meta.approval_modes || []).map((m) => ({
      label: m.label,
      sub: m.hint,
      patch: { approval_mode: m.key },
      recommended: m.key === 'review_deals'
    }))
  },
  {
    key: 'max_auto_replies',
    title: 'Auto replies before a check',
    ask: () => 'How many automatic replies in one conversation before a person checks in?',
    hint: 'After this many, the conversation waits for you.',
    placeholder: 'e.g. 10',
    options: () => [5, 8, 12].map((n) => ({ label: `${n} replies`, patch: { max_auto_replies: n }, recommended: n === 8 })),
    parse: (text) => {
      const n = firstNumber(text);
      if (n == null) return null;
      if (n < 1 || n > 50) return { error: 'Somewhere between 1 and 50.' };
      return { label: `${n} replies`, patch: { max_auto_replies: n } };
    }
  }
];

const INTRO = 'Hi! Let\'s set up your agent team. I will ask one thing at a time: click a suggestion or type your own answer. Anything you answer can be changed later in Setup.';

// The questions that apply, given the answers so far: no fee range for a team that pays no fees.
const activeFor = (form) => STEPS.filter((s) => !s.when || s.when(form));

// Answers are kept by question, so a draft from before a question was added still fits.
const STEP_KEYS = new Set(STEPS.map((s) => s.key));
// Questions no longer asked: a saved chat that answered them still opens, without those answers.
const RETIRED_KEYS = new Set(['email_footer']);
const draftFits = (d) => !!d && !!d.form && Array.isArray(d.history) && d.history.every((h) => h && (STEP_KEYS.has(h.key) || RETIRED_KEYS.has(h.key)))
  && new Set(d.history.map((h) => h.key)).size === d.history.length;

export const takeLocalDraft = () => {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY));
    localStorage.removeItem(DRAFT_KEY);
    return draftFits(d) && d.history.length ? d : null;
  } catch {
    return null;
  }
};

// The name a chat goes by in the history list.
const chatTitle = (form, brand, copiedFrom) => form.name || (brand ? `${brand.brand_name} team` : copiedFrom ? `Copy of ${copiedFrom.name}` : 'New team');

const SIGN = { GBP: '£', EUR: '€', USD: '$' };

// ── pieces ──────────────────────────────────────────────────────────────────

// `who` names a speaker other than the setup assistant: the deal desk, while a deal is made here.
const AssistantBubble = ({ children, hint, tone, who }) => (
  <div className="flex items-start gap-2.5">
    <div className={`flex h-7 w-7 flex-none items-center justify-center rounded-full text-white ${who ? 'bg-violet-600' : 'bg-blue-600'}`}>
      {who ? <MessageSquare className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
    </div>
    <div className={`max-w-[85%] rounded-2xl rounded-tl-sm border px-3.5 py-2.5 text-sm ${tone === 'warn'
      ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
      : 'border-gray-200 bg-white text-gray-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'}`}>
      {who && <div className="mb-0.5 text-xs font-medium text-violet-700 dark:text-violet-300">{who}</div>}
      <div className="whitespace-pre-wrap">{children}</div>
      {hint && <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">{hint}</div>}
    </div>
  </div>
);

const UserBubble = ({ children, onEdit }) => (
  <div className="group flex items-center justify-end gap-2">
    {onEdit && (
      <button type="button" onClick={onEdit} title="Change this answer"
        className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:text-blue-600 group-hover:opacity-100 focus:opacity-100 dark:text-slate-500 dark:hover:text-blue-300">
        <Pencil className="h-3.5 w-3.5" />
      </button>
    )}
    <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-tr-sm bg-blue-600 px-3.5 py-2.5 text-sm text-white">{children}</div>
  </div>
);

const Suggestion = ({ children, sub, recommended, on, disabled, onClick }) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className={`rounded-xl border px-3 py-1.5 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${on
      ? 'border-blue-500 bg-blue-50 text-blue-800 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-100'
      : 'border-gray-200 bg-white text-gray-800 hover:border-blue-300 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-blue-800 dark:hover:bg-blue-950/40'}`}>
    <span className="flex items-center gap-1.5">
      {on && <Check className="h-3.5 w-3.5 flex-none" />}
      <span>{children}</span>
      {recommended && <span className="rounded-full bg-emerald-100 px-1.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Recommended</span>}
    </span>
    {sub && <span className="mt-0.5 block text-xs text-gray-500 dark:text-slate-400">{sub}</span>}
  </button>
);

// How many matching brands to show as suggestions while typing; the list has them all.
const BRAND_CHIPS = 8;

// Asked again when a team's setup is copied to another brand: they are about the brand itself.
const PER_BRAND = ['brand', 'name', 'brand_pitch', ...STEPS.filter((s) => s.key.startsWith('fact_')).map((s) => s.key)];

// ── the chat ────────────────────────────────────────────────────────────────

// `chatId` and `initial` are the saved chat this opens (null for a new one); `onSaved` hears every save,
// and `onNewChat(state)` opens another chat, for the next brand once a team is made.
const TeamChat = ({ meta, teams = [], chatId = null, initial = null, onSaved, onNewChat, onCreated, onOpenTeam, onUseForm, onUpgrade }) => {
  const [draft] = useState(() => (draftFits(initial) ? initial : null));
  const [form, setForm] = useState(() => {
    const { email_footer: _retired, ...saved } = draft?.form || {};
    return { ...EMPTY, ...saved };
  });
  const [history, setHistory] = useState(() => (draft?.history || []).filter((h) => !RETIRED_KEYS.has(h.key)));
  const [resumed, setResumed] = useState(() => (draft?.history.length || 0) > 0 && !draft?.created);
  const [editing, setEditing] = useState(null); // key of an earlier answer being changed
  const [input, setInput] = useState('');
  const [note, setNote] = useState(null); // "didn't catch that", until the next answer
  // A message the chat will not act on (a plan upgrade, a change to MARS): answered, never recorded (guard.js).
  const [aside, setAside] = useState(null);
  const [picked, setPicked] = useState([]);
  const [brands, setBrands] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // The team this chat made: the chat then offers the next brand.
  const [created, setCreated] = useState(() => draft?.created || null);
  // The team a setup was copied from, while its brand questions are asked again.
  const [copiedFrom, setCopiedFrom] = useState(() => draft?.copiedFrom || null);
  const [saveError, setSaveError] = useState(null);
  const savedId = useRef(chatId);
  const saveQueue = useRef(Promise.resolve()); // one save at a time, so a new chat is created once
  const firstRun = useRef(true);
  // A deal being made in this chat with the deal desk: { offer } (null until the first message), plus
  // `aside`, the desk's answer to a first message it would not act on. Null when the desk is not here.
  const [desk, setDesk] = useState(() => (draft?.desk ? { offer: null } : null));
  const [deskPending, setDeskPending] = useState(null); // message waiting for the desk
  const [savingDeal, setSavingDeal] = useState(false);
  const [dealNote, setDealNote] = useState(null); // "saved and picked", until the next answer
  const deskView = useRef(0); // bumps when the desk opens or closes, so a late answer is dropped
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    Promise.allSettled([
      api('/brands').then((d) => setBrands(d.brands || [])),
      api('/mailboxes').then((d) => setMailboxes(d.mailboxes || [])),
      api('/offers?status=active').then((d) => setDeals(d.offers || [])),
      // A deal the desk was making here when the page closed: its conversation is on the server.
      draft?.desk?.offerId
        ? api(`/offers/${draft.desk.offerId}`).then((d) => setDesk(d.offer && d.offer.status === 'draft' ? { offer: d.offer } : null))
        : null
    ]).then(() => setLoaded(true));
    // Once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The question in front of the user: the one being changed, else the first one not answered yet.
  const active = activeFor(form);
  const TOTAL = active.length;
  const answeredKeys = new Set(history.map((h) => h.key));
  const step = editing ? STEPS.find((s) => s.key === editing) || null : active.find((s) => !answeredKeys.has(s.key)) || null;
  const stepKey = step?.key || null;
  const marketName = (code) => (meta.markets || []).find((m) => m.code === code)?.name || code;
  const brand = brands.find((b) => String(b.id) === String(form.brand_id)) || null;
  // Fees are in the currency of the deals the team offers.
  const cur = deals.find((d) => (form.offer_ids || []).includes(d.id) && d.currency)?.currency || null;
  const ctx = { form, brands, mailboxes, deals, meta, brand, marketName, cur, sym: SIGN[cur] || '' };

  // A multi-pick question starts from what was picked before (going back, or a resumed draft).
  useEffect(() => {
    if (step?.multi) setPicked(step.initial(ctx) || []);
    // Only when the question changes, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, loaded]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [history.length, stepKey, note, loaded, created, desk?.offer?.chat?.length, desk?.aside, deskPending, dealNote]);

  useEffect(() => {
    if (loaded && inputRef.current) inputRef.current.focus();
  }, [stepKey, loaded, !!desk]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (label, patch) => {
    const next = { ...form, ...patch };
    const entry = { key: step.key, q: step.ask(ctx), label, ack: step.ack ? step.ack(next, { ...ctx, form: next }) : null };
    // An answer can take questions away ("no listing fees" drops the fee range): their answers go with them.
    const stillAsked = new Set(activeFor(next).map((s) => s.key));
    setForm(next);
    setHistory((h) => (h.some((x) => x.key === entry.key) ? h.map((x) => (x.key === entry.key ? entry : x)) : [...h, entry])
      .filter((x) => stillAsked.has(x.key)));
    setEditing(null);
    setInput('');
    setNote(null);
    setAside(null);
    setDealNote(null);
    setError(null);
  };

  // ── making a deal here, with the deal desk ──
  const openDesk = () => {
    deskView.current += 1;
    setDesk({ offer: null });
    setDeskPending(null);
    setInput('');
    setNote(null);
    setAside(null);
    setDealNote(null);
    setError(null);
  };

  // Back to picking deals. An unsaved deal stays a draft on the Deals tab.
  const closeDesk = () => {
    deskView.current += 1;
    if (desk?.offer) setDealNote(`The deal is kept as a draft on the Deals tab, to finish any time. ${deals.length ? 'Pick a saved deal, or skip for now.' : 'Skip for now, and pick it in Setup once it is saved.'}`);
    setDesk(null);
    setDeskPending(null);
    setInput('');
    setError(null);
  };

  // Saved, from the chat or the button: it joins the list and is picked for this team.
  const dealSaved = (offer) => {
    deskView.current += 1;
    setDeals((list) => [offer, ...list.filter((d) => d.id !== offer.id)]);
    setPicked((p) => (p.includes(offer.id) ? p : [...p, offer.id]));
    setDesk(null);
    setDeskPending(null);
    setInput('');
    setDealNote(`${offer.name || 'The deal'} is saved and picked for this team. Pick any other deals it may offer, make another one, or press Done.`);
  };

  const sendToDesk = async (text) => {
    const message = (text ?? input).trim();
    if (!message || deskPending || !desk) return;
    const token = deskView.current;
    const before = desk.offer;
    setDeskPending(message);
    setInput('');
    setError(null);
    try {
      const d = await api('/offers/chat', { method: 'POST', body: { offer_id: before?.id, message } });
      if (deskView.current !== token) return;
      // Not a deal message (a plan upgrade, a change to MARS): answered, and no deal is started.
      if (!d.offer) { setDesk({ offer: before, aside: { message, reply: d.reply } }); return; }
      const chat = Array.isArray(d.offer.chat) ? d.offer.chat
        : [...(before?.chat || []), { role: 'user', content: message }, { role: 'assistant', content: d.reply }];
      const offer = { ...d.offer, chat, ready: d.ready ?? d.offer.ready, missing: d.missing ?? d.offer.missing };
      // A yes in the chat saves the deal, the same as the button.
      if (d.saved) dealSaved(offer);
      else setDesk({ offer });
    } catch (err) {
      if (deskView.current === token) {
        setError(err.message);
        setInput(message);
      }
    } finally {
      if (deskView.current === token) setDeskPending(null);
    }
  };

  const saveDeal = async () => {
    if (!desk?.offer) return;
    setSavingDeal(true);
    setError(null);
    try {
      const d = await api(`/offers/${desk.offer.id}/save`, { method: 'POST', body: {} });
      dealSaved(d.offer);
    } catch (err) {
      setError(/name/i.test(err.message) ? `${err.message} Tell the deal desk, e.g. "Rename it UK Casino RevShare".` : err.message);
    } finally {
      setSavingDeal(false);
    }
  };

  const options = step && !step.multi && step.options ? step.options(ctx) : [];
  const items = step?.multi ? step.items(ctx) : [];

  const answerText = () => {
    const text = input.trim();
    if (!text || !step) return;
    // Not a setup answer, and not something this chat does: say so, ask the same question again, record nothing.
    const off = outOfScope(text);
    if (off) {
      setAside({ message: text, ...off });
      setInput('');
      setNote(null);
      return;
    }
    setAside(null);
    if (step.multi) {
      const hits = pickByText(items.filter((x) => !x.disabled), text, (x) => [x.label]);
      if (hits.length === 1) {
        const id = hits[0].id;
        setPicked((p) => (p.includes(id) ? p : [...p, id]));
        setInput('');
        setNote(null);
      } else {
        setNote(hits.length ? 'More than one fits. Click the one you mean.' : 'None of them is called that. Click one, or Done to skip.');
      }
      return;
    }
    if (step.parse) {
      const r = step.parse(text, ctx);
      if (r?.error) { setNote(r.error); return; }
      // Part of the answer (a logo added): the same question stays open.
      if (r?.stay) { setForm((f) => ({ ...f, ...r.patch })); setInput(''); setNote(r.note || null); return; }
      if (r) { commit(r.label ?? text, r.patch); return; }
    }
    const hits = pickByText(options, text, (o) => o.match || [o.label]);
    if (hits.length === 1) { commit(hits[0].label, hits[0].patch); return; }
    setNote(hits.length > 1
      ? `More than one fits: ${hits.slice(0, 5).map((h) => h.label).join(', ')}. Which one?`
      : step.retry || 'I did not catch that. Pick one of the suggestions, or put it another way.');
  };

  const finishMulti = () => {
    const r = step.finish(picked, ctx);
    commit(r.label, r.patch);
  };

  const back = () => {
    setNote(null);
    setAside(null);
    setDealNote(null);
    setError(null);
    if (editing != null) { setEditing(null); return; }
    setHistory((h) => h.slice(0, -1));
  };

  const clearChat = () => {
    deskView.current += 1;
    setDesk(null);
    setDeskPending(null);
    setDealNote(null);
    setEditing(null);
    setInput('');
    setNote(null);
    setAside(null);
    setError(null);
    setResumed(false);
    setCreated(null);
  };

  const startOver = () => {
    // The chat that made a team stays in the history as it is: the next one is a new chat.
    if (created && onNewChat) { onNewChat(null); return; }
    if (!created && history.length && !window.confirm('Start over? Your answers so far are cleared.')) return;
    setForm({ ...EMPTY });
    setHistory([]);
    setCopiedFrom(null);
    clearChat();
  };

  // The same setup for another brand: every answer kept but the brand, the name and the pitch.
  const copyToAnotherBrand = () => {
    if (onNewChat) {
      onNewChat({
        form: { ...form, brand_id: '', name: '', brand_pitch: '', brand_facts: {} },
        history: history.filter((x) => !PER_BRAND.includes(x.key)),
        copiedFrom: { id: created.id, name: created.name }
      });
      return;
    }
    setCopiedFrom(created);
    setForm((f) => ({ ...f, brand_id: '', name: '', brand_pitch: '', brand_facts: {} }));
    setHistory((h) => h.filter((x) => !PER_BRAND.includes(x.key)));
    clearChat();
  };

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      // The signature links and logos answered here go on every mailbox.
      const { signature_extras: extras = {}, ...rest } = form;
      const { logo_url: _single, ...links } = extras;
      const body = {
        ...rest,
        senders: form.senders.map((s) => ({ ...s, ...links, logos: cleanLogos(logosOf(extras)), mailbox_id: Number(s.mailbox_id) })),
        followup_days: (form.followup_days || []).map(Number)
      };
      const data = await api('/teams', { method: 'POST', body });
      setCreated(data.team);
      onCreated(data.team);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // What shows as suggestions. Brands can run long: they are all in the list, and typing shows the few that match.
  const shownOptions = step?.searchable && input.trim()
    ? pickByText(options, input, (o) => o.match || [o.label])
    : options;
  const visibleOptions = step?.searchable ? (input.trim() ? shownOptions.slice(0, BRAND_CHIPS) : []) : shownOptions;
  const hiddenCount = step?.searchable && input.trim() ? shownOptions.length - visibleOptions.length : 0;
  // Brands that already have a team say so in the list; a brand can still have more than one.
  const teamFor = (brandId) => teams.find((t) => String(t.brand_id) === String(brandId));

  const answered = active.filter((s) => answeredKeys.has(s.key)).length;
  const pct = TOTAL ? Math.round((answered / TOTAL) * 100) : 0;

  // Saved after every answer. A chat opened from the list is not written back until something changes,
  // and a new chat is not kept until it has an answer.
  const deskOfferId = desk ? desk.offer?.id || null : undefined;
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      if (savedId.current) return;
    }
    if (!history.length && !savedId.current) return;
    const body = {
      title: chatTitle(form, brand, copiedFrom),
      progress: created ? 100 : pct,
      team_id: created?.id || null,
      state: {
        form,
        history,
        desk: deskOfferId === undefined ? null : { offerId: deskOfferId },
        created: created ? { id: created.id, name: created.name } : null,
        copiedFrom: copiedFrom ? { id: copiedFrom.id, name: copiedFrom.name } : null
      }
    };
    saveQueue.current = saveQueue.current.then(async () => {
      try {
        const d = savedId.current
          ? await api(`/setup-chats/${savedId.current}`, { method: 'PUT', body })
          : await api('/setup-chats', { method: 'POST', body });
        savedId.current = d.chat.id;
        setSaveError(null);
        if (onSaved) onSaved(d.chat);
      } catch (err) {
        setSaveError(err.message);
      }
    });
    // The chat itself, not what is derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, history, deskOfferId, created, copiedFrom]);
  const hintText = step ? (typeof step.hint === 'function' ? step.hint(ctx) : step.hint) : null;
  const askText = step ? step.ask(ctx) : null;
  // The deal desk sits in on the deals question only.
  const onDealStep = stepKey === 'offer_ids';
  const deskOn = onDealStep && !!desk;
  const dealButton = onDealStep && !desk && !saving && (
    <span className="mt-2 block">
      <Button icon={Plus} variant={deals.length ? undefined : 'primary'} onClick={openDesk}>{deals.length ? 'Make a new deal here' : 'Make a deal here'}</Button>
    </span>
  );
  const deskMissing = desk?.offer?.missing || [];

  return (
    <div className="flex h-full min-h-[30rem] w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 shadow-sm dark:border-slate-800 dark:bg-slate-950">
      {/* Progress: which question this is, and how much is done. */}
      <div className="flex-none border-b border-gray-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-4 w-4 flex-none text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">{copiedFrom ? `New team, copied from ${copiedFrom.name}` : 'New team'}</span>
          <span className="truncate text-xs text-gray-500 dark:text-slate-400">
            {created ? 'Team created' : deskOn ? 'Now: Making a deal with the deal desk' : step ? `${editing != null ? 'Changing: ' : 'Now: '}${step.title}` : 'Ready to create'}
          </span>
          {saveError && <span className="ml-auto truncate text-xs text-amber-700 dark:text-amber-300" title={saveError}>Not saved yet: {saveError} It is tried again with the next answer.</span>}
          <span className={`${saveError ? '' : 'ml-auto '}text-sm font-semibold tabular-nums text-blue-700 dark:text-blue-300`}>{created ? 100 : pct}%</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-slate-800"
          role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={created ? 100 : pct} aria-label="Team setup progress">
          <div className={`h-full rounded-full transition-all duration-500 ${pct === 100 || created ? 'bg-emerald-500' : 'bg-blue-600 dark:bg-blue-500'}`} style={{ width: `${created ? 100 : pct}%` }} />
        </div>
      </div>

      {/* The conversation so far. */}
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
        <AssistantBubble>{INTRO}</AssistantBubble>
        {copiedFrom && (
          <AssistantBubble>{`Same setup as ${copiedFrom.name}: deals, mailboxes, timing and rules are kept. I only need what is different for the new brand: the brand, a name for the team, why affiliates should list it, and its payments, withdrawals, games, app and marketing material. Any answer below can still be changed.`}</AssistantBubble>
        )}
        {resumed && answered > 0 && (
          <div className="text-center text-xs text-gray-500 dark:text-slate-400">
            Picked up where you left off. <button type="button" className="underline hover:text-gray-700 dark:hover:text-slate-200" onClick={startOver}>Start over</button>
          </div>
        )}
        {history.map((h) => (
          <React.Fragment key={h.key}>
            <AssistantBubble>{h.q}</AssistantBubble>
            <UserBubble onEdit={saving || created ? null : () => { setEditing(h.key); setNote(null); setInput(''); }}>{h.label}</UserBubble>
            {h.ack && <AssistantBubble>{h.ack}</AssistantBubble>}
            {/* While an earlier answer is being changed, ask it again right under it. */}
            {editing === h.key && step && <AssistantBubble hint={hintText}>{`Sure, let's change that. ${askText}`}{dealButton}</AssistantBubble>}
          </React.Fragment>
        ))}

        {!loaded ? (
          <AssistantBubble>One moment, getting your brands, deals and mailboxes...</AssistantBubble>
        ) : created ? (
          <AssistantBubble>
            {`${created.name} is created, paused: nothing is sent until you press Start team. Want another brand too? Copy this setup to it, and I will only ask what is different, or start a new one from scratch.`}
          </AssistantBubble>
        ) : step && editing == null ? (
          <AssistantBubble hint={hintText}>
            {askText}
            {dealButton}
          </AssistantBubble>
        ) : !step && (
          <>
            <AssistantBubble>That's everything. Here is the team; click any answer above to change it.</AssistantBubble>
            <div className="ml-9 rounded-xl border border-gray-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
              <dl className="divide-y divide-gray-100 text-sm dark:divide-slate-800">
                {active.map((s) => {
                  const label = history.find((h) => h.key === s.key)?.label;
                  return (
                    <div key={s.key} className="flex gap-3 py-1.5">
                      <dt className="w-40 flex-none text-gray-500 dark:text-slate-400">{s.title}</dt>
                      <dd className="min-w-0 flex-1 truncate text-gray-800 dark:text-slate-200" title={label}>{label}</dd>
                      <button type="button" className="flex-none text-xs text-blue-600 hover:underline dark:text-blue-400" onClick={() => setEditing(s.key)}>Change</button>
                    </div>
                  );
                })}
              </dl>
              <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                The team is created paused. Nothing is sent until you press Start team.
              </p>
            </div>
          </>
        )}
        {/* A deal being made here: the deal desk's side of the chat, and the deal as it stands. */}
        {loaded && deskOn && (
          <>
            <AssistantBubble who="Deal desk">
              {`Hi, I'm the deal desk. Tell me the deal${brand ? ` for ${brand.brand_name}` : ''} the way you would tell a colleague: who it is for, the structure (RevShare, CPA or hybrid) and how far you can go. I will ask about anything missing, then read it back for your yes.`}
            </AssistantBubble>
            {(desk.offer?.chat || []).map((m, i) => (m.role === 'user'
              ? <UserBubble key={`${i}-${m.at || ''}`}>{m.content}</UserBubble>
              : <AssistantBubble key={`${i}-${m.at || ''}`} who="Deal desk">{m.content}</AssistantBubble>))}
            {desk.aside && !deskPending && (
              <>
                <UserBubble>{desk.aside.message}</UserBubble>
                <AssistantBubble who="Deal desk" tone="warn">{desk.aside.reply}</AssistantBubble>
              </>
            )}
            {deskPending && (
              <>
                <UserBubble>{deskPending}</UserBubble>
                <div className="ml-9 flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />Deal desk is thinking...
                </div>
              </>
            )}
            {desk.offer && !deskPending && (
              <div className="ml-9 rounded-xl border border-violet-200 bg-white p-3 text-sm dark:border-violet-900 dark:bg-slate-900">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-slate-100">{desk.offer.name || 'New deal'}</span>
                  <span className="rounded-full bg-amber-100 px-2 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Not saved yet</span>
                </div>
                {desk.offer.summary && <p className="mt-1 text-gray-600 dark:text-slate-400">{desk.offer.summary}</p>}
                {deskMissing.length > 0 ? (
                  <div className="mt-2 text-amber-800 dark:text-amber-200">
                    <div className="text-xs font-medium">Still needed:</div>
                    <ul className="mt-0.5 space-y-0.5">
                      {deskMissing.map((m) => <li key={m} className="flex items-center gap-1.5"><Circle className="h-3 w-3 flex-none" />{m}</li>)}
                    </ul>
                  </div>
                ) : desk.offer.ready && (
                  <p className="mt-2 text-emerald-700 dark:text-emerald-300">The deal desk has everything. Say yes in the chat, or press Save deal, and it is picked for this team.</p>
                )}
              </div>
            )}
          </>
        )}
        {dealNote && <AssistantBubble>{dealNote}</AssistantBubble>}
        {aside && (
          <>
            <UserBubble>{aside.message}</UserBubble>
            <AssistantBubble tone="warn">
              {`${aside.reply} Here I can only set up your agent team, so let's carry on.${askText ? ` ${askText}` : ''}`}
              {aside.key === 'plan' && onUpgrade && (
                <span className="mt-2 block"><Button onClick={onUpgrade}>See the plans</Button></span>
              )}
            </AssistantBubble>
          </>
        )}
        {note && <AssistantBubble tone="warn">{note}</AssistantBubble>}
      </div>

      {/* Suggestions and the answer box. */}
      <div className="flex-none border-t border-gray-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
        {error && <div className="mb-2"><Notice tone="error">{error}</Notice></div>}

        {loaded && step?.searchable && options.length > 0 && (
          <select className={`${inputCls} mb-2`} aria-label="Pick a brand"
            value={String(form.brand_id || '')}
            onChange={(e) => {
              const o = options.find((x) => String(x.patch.brand_id) === e.target.value);
              if (o) commit(o.label, o.patch);
            }}>
            <option value="">Pick one of your {options.length} brands...</option>
            {options.map((o) => {
              const taken = teamFor(o.patch.brand_id);
              return (
                <option key={o.patch.brand_id} value={String(o.patch.brand_id)}>
                  {[o.label, o.sub, taken ? `has a team: ${taken.name}` : null].filter(Boolean).join(' · ')}
                </option>
              );
            })}
          </select>
        )}

        {loaded && step && !step.multi && visibleOptions.length > 0 && (
          <div className="mb-2 flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
            {visibleOptions.map((o, i) => (
              <Suggestion key={`${i}-${o.label}`}sub={o.sub} recommended={o.recommended} onClick={() => commit(o.label, o.patch)}>{o.label}</Suggestion>
            ))}
            {hiddenCount > 0 && <span className="self-center text-xs text-gray-500 dark:text-slate-400">and {hiddenCount} more: keep typing, or pick from the list</span>}
          </div>
        )}
        {loaded && step?.upload && (
          <div className="mb-2">
            <LogoEditor logos={logosOf(form.signature_extras)}
              onChange={(logos) => { setNote(null); setForm((f) => ({ ...f, signature_extras: { ...f.signature_extras, logos } })); }} />
          </div>
        )}
        {loaded && step?.searchable && input.trim() && !shownOptions.length && (
          <p className="mb-2 text-xs text-gray-500 dark:text-slate-400">No brand matches "{input.trim()}".</p>
        )}

        {loaded && step?.multi && !deskOn && (
          <div className="mb-2 flex max-h-44 flex-wrap items-start gap-1.5 overflow-y-auto">
            {items.map((x) => {
              const on = picked.includes(x.id);
              const position = picked.indexOf(x.id);
              return (
                <Suggestion key={x.id} on={on} disabled={x.disabled}
                  sub={[on && step.key === 'offer_ids' && position === 0 ? 'Default' : null, x.sub].filter(Boolean).join(' · ') || null}
                  onClick={() => setPicked((p) => (on ? p.filter((id) => id !== x.id) : [...p, x.id]))}>
                  {x.label}
                </Suggestion>
              );
            })}
          </div>
        )}

        {deskOn ? (
          <>
            {!deskPending && (
              <div className="mb-2 flex max-h-20 flex-wrap gap-1.5 overflow-y-auto">
                <span className="self-center text-xs text-gray-500 dark:text-slate-400">{desk.offer ? 'Change it:' : 'For example:'}</span>
                {(desk.offer ? DEAL_EDITS : DEAL_EXAMPLES).map((t) => (
                  <button key={t} type="button"
                    onClick={() => { setInput(t); if (inputRef.current) inputRef.current.focus(); }}
                    className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-700 hover:border-violet-300 hover:bg-violet-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-violet-800 dark:hover:bg-violet-950/40">
                    {t.trim()}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <Button icon={Undo2} onClick={closeDesk} disabled={savingDeal} title="Back to picking the team's deals">Back</Button>
              <textarea ref={inputRef} rows={2} className={`${inputCls} flex-1 resize-none`}
                placeholder={desk.offer
                  ? 'Answer the deal desk, or tell it what to change. Say yes to its summary and the deal is saved.'
                  : 'Describe the deal: who it is for, the structure and how far you can go'}
                value={input} disabled={!!deskPending || savingDeal}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    sendToDesk();
                  }
                }} />
              <Button variant="primary" icon={Send} busy={!!deskPending} disabled={!input.trim() || savingDeal} onClick={() => sendToDesk()}>Send</Button>
            </div>
            {desk.offer?.ready && (
              <div className="mt-2 flex items-center justify-end gap-2">
                <span className="text-xs text-gray-500 dark:text-slate-400">The budget stays internal: affiliates never see it.</span>
                <Button variant="success" icon={Save} busy={savingDeal} disabled={!!deskPending} onClick={saveDeal}>Save deal</Button>
              </div>
            )}
          </>
        ) : step ? (
          <div className="flex items-end gap-2">
            {(answered > 0 || editing != null) && (
              <Button icon={Undo2} onClick={back} title={editing != null ? 'Keep the answer as it was' : 'Back to the previous question'}>
                {editing != null ? 'Cancel' : 'Back'}
              </Button>
            )}
            {step.multi ? (
              <>
                <div className="flex-1 text-xs text-gray-500 dark:text-slate-400">
                  {picked.length ? `${picked.length} picked.` : items.length ? 'Pick one or more above.' : onDealStep ? 'No saved deal yet: make one above.' : 'Nothing to pick yet.'}
                </div>
                <Button variant="primary" icon={Check} onClick={finishMulti} disabled={!loaded}>
                  {picked.length ? 'Done' : 'Skip for now'}
                </Button>
              </>
            ) : (
              <>
                {step.multiline ? (
                  <textarea ref={inputRef} rows={2} className={`${inputCls} flex-1 resize-none`}
                    placeholder={step.placeholder || 'Type your answer'}
                    value={input} disabled={!loaded}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        answerText();
                      }
                    }} />
                ) : (
                  <input ref={inputRef} className={`${inputCls} flex-1`}
                    placeholder={step.placeholder || 'Type your answer, or pick a suggestion'}
                    value={input} disabled={!loaded}
                    onChange={(e) => { setInput(e.target.value); if (note) setNote(null); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        answerText();
                      }
                    }} />
                )}
                <Button variant="primary" icon={Send} onClick={answerText} disabled={!input.trim() || !loaded}>Send</Button>
              </>
            )}
          </div>
        ) : created ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" icon={Copy} onClick={copyToAnotherBrand}>Copy this setup to another brand</Button>
            <Button icon={Plus} onClick={startOver}>New team from scratch</Button>
            {onOpenTeam && <Button icon={ArrowRight} onClick={() => onOpenTeam(created)}>Open {created.name}</Button>}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" icon={Rocket} busy={saving} onClick={create}>Create team</Button>
            <Button icon={RotateCcw} onClick={startOver} disabled={saving}>Start over</Button>
          </div>
        )}

        {onUseForm && !created && (
          <div className="mt-2 text-right">
            <button type="button" onClick={onUseForm} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200">
              <FileText className="h-3.5 w-3.5" />Prefer the full form?
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamChat;
